import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { DICTIONARIES, LANGS, isValidLang } from "@/i18n/translations";
import FilmGrain from "@/components/FilmGrain";

const LanguageLanding = () => {
  const navigate = useNavigate();

  // Auto-redirect if user already chose a language
  useEffect(() => {
    const stored = localStorage.getItem("em_lang");
    if (stored && isValidLang(stored)) {
      navigate(`/${stored}`, { replace: true });
    }
  }, [navigate]);

  const onPick = (code) => {
    localStorage.setItem("em_lang", code);
    navigate(`/${code}`);
  };

  return (
    <div
      className="relative min-h-screen flex flex-col bg-background"
      data-testid="language-landing"
    >
      <FilmGrain />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 10%, rgba(184,155,114,0.10), transparent 55%), radial-gradient(900px 500px at 80% 30%, rgba(31,31,31,0.06), transparent 60%)",
        }}
      />
      <div className="px-5 sm:px-8 py-6 flex items-center justify-between relative z-10">
        <span className="editorial-kicker">A private wedding journal</span>
        <span className="editorial-kicker hidden sm:inline">19 · 05 · 2027</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 relative z-10">
        <div className="w-full max-w-[640px] text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="editorial-kicker"
          >
            {DICTIONARIES.en.landing.overline}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.05 }}
            className="editorial-h1 mt-6 text-[56px] sm:text-[80px] lg:text-[112px] leading-[0.98] tracking-[0.01em]"
          >
            Elias <span className="text-accent italic">&amp;</span> Magdalena
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="editorial-kicker mt-10"
          >
            Choose your language
          </motion.p>

          <motion.ul
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: { staggerChildren: 0.08, delayChildren: 0.25 },
              },
            }}
            className="mt-8 mx-auto max-w-[420px] divide-y divide-border"
          >
            {LANGS.map((l) => (
              <motion.li
                key={l.code}
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
                }}
              >
                <button
                  type="button"
                  onClick={() => onPick(l.code)}
                  data-testid={`language-landing-lang-button-${l.code}`}
                  className="group w-full py-5 flex items-baseline justify-between transition-[letter-spacing] duration-300 hover:tracking-[0.04em]"
                >
                  <span className="font-serif-display text-3xl sm:text-4xl tracking-[0.02em] group-hover:text-accent transition-colors">
                    {l.label}
                  </span>
                  <span className="text-[11px] tracking-[0.28em] uppercase text-foreground/45 group-hover:text-foreground transition-colors">
                    {l.short}
                  </span>
                </button>
              </motion.li>
            ))}
          </motion.ul>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.0, delay: 0.7 }}
            className="editorial-kicker mt-14"
          >
            19 May 2027 · Ammos Kalambouri Beach · Cyprus
          </motion.p>
          <p className="editorial-kicker mt-3 text-foreground/40">
            <Link to="/admin" data-testid="admin-entry-link" className="hover:text-foreground">
              for the couple
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LanguageLanding;
